import { UTCDate } from '@date-fns/utc';

import { type Filter, FilterType } from '../types/filters';

/**
 * Revive a persisted date bound.
 *
 * Persisted filter state (deeplink `filters=` param, saved views) is
 * JSON, so a `UTCDate` bound round-trips to an ISO string. Consumers type it
 * as `UTCDate` and call date methods on it, so an unrevived string throws.
 * An unparseable bound becomes `null` rather than an Invalid Date, which
 * would otherwise reach the API as a NaN range.
 */
const reviveDateBound = (bound: unknown): UTCDate | null => {
  if (bound == null) return null;
  if (bound instanceof Date) return bound as UTCDate;
  if (typeof bound !== 'string') return null;

  const parsed = new UTCDate(bound);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Normalize the date bounds of a single filter.
 *
 * Keyed on the filter TYPE, not the id, so every date-carrying filter is
 * covered: `DATE_RANGE` and `POST_DATE` share the range shape, `SNAPSHOT_DATE`
 * the single-value one. Each bound is revived independently so a one-sided
 * range still lands.
 *
 * Returns the filter unchanged when nothing needed reviving, so a map of live
 * filters keeps its identities.
 */
export const reviveFilterDates = (filter: Filter): Filter => {
  if (filter.type === FilterType.DATE_RANGE) {
    const min = reviveDateBound(filter.min);
    const max = reviveDateBound(filter.max);
    return min === filter.min && max === filter.max
      ? filter
      : { ...filter, min, max };
  }

  if (filter.type === FilterType.DATE) {
    const value = reviveDateBound(filter.value);
    return value === filter.value ? filter : { ...filter, value };
  }

  return filter;
};
